import { User } from 'module/api/user/types';
import { getUserLabel } from 'module/helpers/get-user-label';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Popover } from 'module/ui/components/atoms/popover';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { Avatar, Loader } from '@rocketlane/ui';
import {
  EmailMessage as EmailMessageType,
  EmailMessageAttachment,
} from 'api/email-messages/types';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { EmailAttachment, RenderEmailAttachment } from './email-attachment';
import { EmailHtmlContent } from './email-html-content';

const Row = styled.div<{ $hideBorderBottom?: boolean }>`
  padding: var(--spacing-05) 0;
  border-bottom: ${({ $hideBorderBottom }) =>
    $hideBorderBottom ? 'none' : '1px solid var(--field-border-default)'};
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
`;

const NameSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const MessageBody = styled.div`
  padding-left: calc(32px + var(--spacing-04));
`;

const CollapsedRow = styled.button<{ $hideBorderBottom?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-04);
  width: 100%;
  padding: var(--spacing-04) 0;
  border: none;
  border-bottom: ${({ $hideBorderBottom }) =>
    $hideBorderBottom ? 'none' : '1px solid var(--field-border-default)'};
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: transparent;
  }
`;

const CollapsedBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
`;

const CollapsedTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const CollapsedSender = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CollapsedSnippet = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const CollapsedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  color: var(--text-tertiary);
  font-size: 12px;
  flex-shrink: 0;
  margin-left: var(--spacing-03);
`;

const HeaderLine = styled.div<{ $collapsible?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-03);
  cursor: ${({ $collapsible }) => ($collapsible ? 'pointer' : 'default')};

  &:hover {
    opacity: ${({ $collapsible }) => ($collapsible ? '0.85' : '1')};
  }
`;

const SenderName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const MetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  color: var(--text-tertiary);
  font-size: 12px;
`;

const RecipientsTrigger = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  margin-top: var(--spacing-01);
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-02);
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
`;

const BodyText = styled.div`
  font-size: 14px;
  color: var(--text-primary);
  margin-top: var(--spacing-03);
  white-space: pre-wrap;
  line-height: 1.5;
`;

const ContentLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  width: 100%;
  margin-top: var(--spacing-03);
`;

const AttachmentsBlock = styled.div`
  margin-top: var(--spacing-04);
  padding-top: var(--spacing-04);
  border-top: 1px solid var(--field-border-default);
`;

const AttachmentsHeader = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: var(--spacing-03);
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-04);
`;

const DetailsTable = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: var(--spacing-03) var(--spacing-05);
  min-width: 360px;
  max-width: 520px;
  font-size: 13px;
  padding: var(--spacing-04) var(--spacing-05);
`;

const DetailKey = styled.div`
  color: var(--text-secondary);
  text-align: right;
`;

const DetailValue = styled.div`
  color: var(--text-primary);
  word-break: break-word;
`;

const resolveCidSrcs = (
  html: string,
  cidMap: Record<string, number>,
  attachments: EmailMessageAttachment[],
): string => {
  const attachmentById = new Map(attachments.map((a) => [a.attachmentId, a]));

  return html.replace(/src=(["'])cid:([^"']+)\1/gi, (match, quote, cid) => {
    const attachmentId = cidMap[cid];
    const attachment =
      attachmentId !== undefined ? attachmentById.get(attachmentId) : undefined;

    if (!attachment) {
      return match;
    }

    const url = attachment.downloadUrl || attachment.url;

    return `src=${quote}${url}${quote}`;
  });
};

const senderDisplayName = (sender: string) => {
  const local = sender.split('@')[0] ?? sender;

  return local
    .split(/[.\-_]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
};

const formatRecipient = (r: string | User): string => {
  if (typeof r === 'string') {
    return r;
  }
  const hasName = Boolean(r.firstName || r.lastName);

  return hasName ? `${getUserLabel(r)} <${r.emailId}>` : r.emailId;
};

const renderRecipientList = (list: (string | User)[]): string =>
  list.length === 0 ? '—' : list.map(formatRecipient).join(', ');

const extractSnippet = (message: EmailMessageType): string => {
  if (message.body) {
    return message.body.replace(/\s+/g, ' ').trim().slice(0, 120);
  }
  if (message.htmlContent) {
    try {
      const doc = new DOMParser().parseFromString(
        message.htmlContent,
        'text/html',
      );

      return (doc.body.textContent ?? '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);
    } catch {
      return message.subject;
    }
  }

  return message.subject;
};

interface Props {
  message: EmailMessageType;
  renderAttachment?: RenderEmailAttachment;
  collapsed?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  /** Called once this message's content (including images) has settled. */
  onReady?: (messageId: number) => void;
  hideBorderBottom?: boolean;
  /** Called when the user clicks inside the email iframe — lets ancestors close their own popovers. */
  onIframeClick?: () => void;
}

export const EmailMessage: FC<Props> = ({
  message,
  renderAttachment,
  collapsed = false,
  onExpand,
  onCollapse,
  onReady,
  hideBorderBottom,
  onIframeClick,
}) => {
  const displayName = useMemo(
    () => senderDisplayName(message.sender),
    [message.sender],
  );
  const recipients = useMemo(
    () => message.recipients ?? [],
    [message.recipients],
  );
  const attachments = message.attachments ?? [];
  const cc = message.cc ?? [];
  const bcc = message.bcc ?? [];

  const resolvedHtmlContent = useMemo(() => {
    const cidMap = message.metaData?.message_inline_attachments;
    const inlineAttachments = message.inlineAttachments ?? [];

    if (!message.htmlContent || !cidMap || inlineAttachments.length === 0) {
      return message.htmlContent;
    }

    return resolveCidSrcs(message.htmlContent, cidMap, inlineAttachments);
  }, [message.htmlContent, message.metaData, message.inlineAttachments]);

  const recipientsLabel = useMemo(() => {
    const first = recipients.slice(0, 6).map((r) => {
      if (typeof r === 'string') {
        const local = r.split('@')[0];

        return local.charAt(0).toUpperCase() + local.slice(1);
      }

      return getUserLabel(r);
    });
    const extra = recipients.length - first.length;
    const head = first.join(', ');

    return extra > 0 ? `${head}, +${extra}` : head;
  }, [recipients]);

  const hasAttachments = attachments.length > 0;
  const hasHtmlContent = Boolean(message.htmlContent);
  const messageId = message.emailMessageId;

  // Track whether this message's iframe content has finished loading.
  const [isContentReady, setIsContentReady] = useState(!hasHtmlContent);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Reset loader when collapsed state changes (iframe unmounts/remounts).
  useEffect(() => {
    setIsContentReady(!hasHtmlContent);
  }, [collapsed, hasHtmlContent]);

  const handleContentReady = useCallback(() => {
    setIsContentReady(true);
    onReady?.(messageId);
  }, [onReady, messageId]);

  useEffect(() => {
    if (!hasHtmlContent) {
      onReady?.(messageId);
    }
  }, [hasHtmlContent, onReady, messageId]);

  const timestamp = moment(message.externalMessageTimestamp).format(
    'ddd D MMM, hh:mm A',
  );
  const longTimestamp = moment(message.externalMessageTimestamp).format(
    'D MMM YYYY, HH:mm',
  );

  if (collapsed) {
    const snippet =
      message.body || message.htmlContent ? extractSnippet(message) : null;

    return (
      <CollapsedRow
        type="button"
        onClick={onExpand}
        $hideBorderBottom={hideBorderBottom}
      >
        <Avatar size={32} name={displayName} />
        <CollapsedBody>
          <CollapsedTopRow>
            <CollapsedSender>{displayName}</CollapsedSender>
            <CollapsedMeta>
              {hasAttachments && (
                <FontAwesomeIcon icon={faPaperclip} size={12} />
              )}
              <span>{timestamp}</span>
            </CollapsedMeta>
          </CollapsedTopRow>
          {snippet && <CollapsedSnippet>{snippet}</CollapsedSnippet>}
        </CollapsedBody>
      </CollapsedRow>
    );
  }

  const detailsContent = (
    <DetailsTable>
      <DetailKey>from:</DetailKey>
      <DetailValue>
        <strong>{displayName}</strong> &lt;{message.sender}&gt;
      </DetailValue>

      <DetailKey>to:</DetailKey>
      <DetailValue>{renderRecipientList(recipients)}</DetailValue>

      {cc.length > 0 && (
        <>
          <DetailKey>cc:</DetailKey>
          <DetailValue>{renderRecipientList(cc)}</DetailValue>
        </>
      )}

      {bcc.length > 0 && (
        <>
          <DetailKey>bcc:</DetailKey>
          <DetailValue>{renderRecipientList(bcc)}</DetailValue>
        </>
      )}

      <DetailKey>date:</DetailKey>
      <DetailValue>{longTimestamp}</DetailValue>

      {message.subject && (
        <>
          <DetailKey>subject:</DetailKey>
          <DetailValue>{message.subject}</DetailValue>
        </>
      )}
    </DetailsTable>
  );

  return (
    <Row $hideBorderBottom={hideBorderBottom}>
      <MessageHeader>
        <Avatar size={32} name={displayName} />
        <NameSection>
          <HeaderLine
            $collapsible={!!onCollapse}
            onClick={() => {
              setDetailsOpen(false);
              onCollapse?.();
            }}
          >
            <SenderName>{displayName}</SenderName>
            <MetaRight>
              {hasAttachments && (
                <FontAwesomeIcon icon={faPaperclip} size={12} />
              )}
              <span>{timestamp}</span>
            </MetaRight>
          </HeaderLine>

          <Popover
            content={detailsContent}
            placement="bottomLeft"
            trigger="click"
            open={detailsOpen}
            onOpenChange={setDetailsOpen}
            destroyTooltipOnHide
          >
            <RecipientsTrigger type="button" aria-label="Show message details">
              <span>to {recipientsLabel}</span>
              <FontAwesomeIcon icon={faChevronDown} size={10} />
            </RecipientsTrigger>
          </Popover>
        </NameSection>
      </MessageHeader>

      <MessageBody>
        {resolvedHtmlContent ? (
          <>
            {!isContentReady && (
              <ContentLoader>
                <Loader />
              </ContentLoader>
            )}
            <EmailHtmlContent
              html={resolvedHtmlContent}
              onReady={handleContentReady}
              onIframeClick={() => {
                setDetailsOpen(false);
                onIframeClick?.();
              }}
            />
          </>
        ) : (
          <BodyText>{message.body}</BodyText>
        )}

        {hasAttachments && isContentReady && (
          <AttachmentsBlock>
            <AttachmentsHeader>
              {attachments.length} attachment
              {attachments.length === 1 ? '' : 's'}
            </AttachmentsHeader>
            <AttachmentsList>
              {attachments.map((a) => (
                <EmailAttachment
                  key={a.attachmentId}
                  attachment={a}
                  renderAttachment={renderAttachment}
                />
              ))}
            </AttachmentsList>
          </AttachmentsBlock>
        )}
      </MessageBody>
    </Row>
  );
};
